/* eslint-disable react/no-array-index-key */
import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { twMerge } from "tailwind-merge";
import { Nib } from "~/components/shared/Nib";
import Icon from "~/components/shared/Icon";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuArrow = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Arrow>
>(({ ...props }, ref) => (
  <DropdownMenuPrimitive.Arrow ref={ref} asChild={true} {...props} width={16} height={8}>
    <Nib />
  </DropdownMenuPrimitive.Arrow>
));
DropdownMenuArrow.displayName = DropdownMenuPrimitive.Arrow.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ children, className, sideOffset = -3, collisionPadding = 10, ...props }, ref) => (
  <DropdownMenuPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    collisionPadding={collisionPadding}
    arrowPadding={4}
    className={twMerge(
      "shadow-depth-800 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[180px] rounded bg-white p-2 data-[state=closed]:duration-100 data-[state=open]:duration-100",
      className,
    )}
    {...props}
  >
    <DropdownMenuArrow />
    {children}
  </DropdownMenuPrimitive.Content>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    active?: boolean;
  }
>(({ className, active, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    data-active={active}
    className={twMerge(
      "text-charcoal-700 group relative flex w-full cursor-pointer select-none items-center rounded p-2 text-sm font-medium outline-none transition-colors data-[disabled]:pointer-events-none data-[highlighted]:text-purple-600 data-[highlighted]:no-underline data-[disabled]:opacity-50 data-[highlighted]:outline-none",
      active && "bg-purple-100 text-purple-600",
      className,
    )}
    {...props}
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, checked, children, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    data-active={checked}
    className={twMerge(
      "text-charcoal-700 group relative flex w-full cursor-pointer select-none items-center rounded p-2 text-sm font-semibold outline-none transition-colors data-[disabled]:pointer-events-none data-[highlighted]:text-purple-600 data-[highlighted]:no-underline data-[disabled]:opacity-50 data-[highlighted]:outline-none",
      checked && "bg-purple-100 text-purple-600",
      className,
    )}
    {...props}
  >
    <div
      data-active={checked}
      className="mr-2 flex h-[18px] w-[18px] items-center justify-center rounded border border-gray-400 data-[active=true]:bg-purple-600"
    >
      {checked && <Icon icon="heroicons/outline/check" className="h-3 w-3 text-white" />}
    </div>

    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={twMerge(
      "text-charcoal-700 flex items-center p-2 text-xs font-semibold uppercase tracking-wider",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={twMerge("-mx-2 my-2 h-px bg-gray-500", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuPortal,
  DropdownMenuArrow,
};

type ComposedDropdownMenuProps = {
  trigger: string;
  contentProps?: React.ComponentPropsWithoutRef<typeof DropdownMenuContent>;
  triggerProps?: React.ComponentPropsWithoutRef<typeof DropdownMenuTrigger> & {
    as?: React.ElementType;
  };
  items: Array<
    | {
        component: "item";
        props: React.ComponentPropsWithoutRef<typeof DropdownMenuItem> & {
          href?: string;
          children: string;
        };
      }
    | {
        component: "label";
        props: React.ComponentPropsWithoutRef<typeof DropdownMenuLabel>;
      }
    | {
        component: "separator";
        props: React.ComponentPropsWithoutRef<typeof DropdownMenuSeparator>;
      }
  >;
};

export function ComposedDropdownMenu({
  trigger,
  items,
  contentProps,
  triggerProps,
  ...props
}: ComposedDropdownMenuProps) {
  const TriggerTag = triggerProps?.as ?? "button";

  return (
    <DropdownMenu {...props}>
      <DropdownMenuTrigger {...triggerProps} asChild={true}>
        <TriggerTag dangerouslySetInnerHTML={{ __html: trigger }} />
      </DropdownMenuTrigger>

      <DropdownMenuContent {...contentProps}>
        {items.map(({ component, props }, index) => {
          if (component === "item") {
            const ItemTag = props.href ? "a" : "button";

            return (
              <DropdownMenuItem key={index} {...props} asChild={true}>
                <ItemTag dangerouslySetInnerHTML={{ __html: props.children }} />
              </DropdownMenuItem>
            );
          } else if (component === "label") {
            return <DropdownMenuLabel key={index} {...props} />;
          } else if (component === "separator") {
            return <DropdownMenuSeparator key={index} {...props} />;
          }
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
